<script setup>
import appAxios from '@/libs/app-axios'
import { openStream } from '@/libs/sse-stream'
import { computed, onBeforeMount, onBeforeUnmount, reactive, ref } from 'vue'

/** @type {DashboardState} */
const state = reactive({
  data: null,
  isLoading: false,
  error: null,
})

/** @type {AbortController|null} */
let controller = null
/** @type {number|null} */
let retryTimer = null

const getDashboard = async () => {
  controller?.abort()
  controller = new AbortController()

  try {
    state.isLoading = true
    state.error = null
    const { data } = await appAxios().get('/v3/dashboard', { signal: controller.signal })
    state.data = data.data
  } catch (e) {
    if (e?.name === 'CanceledError' || e?.name === 'AbortError') return
    state.error = e?.message || 'Failed to load dashboard'
    retryTimer = setTimeout(() => getDashboard(), 5000)
  } finally {
    state.isLoading = false
  }
}

const monthLabel = computed(() => {
  const month = state.data?.month
  if (!month) return ''
  const [year, m] = month.split('-')
  return new Date(Number(year), Number(m) - 1, 1).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
  })
})

/** @type {import('vue').ComputedRef<StatCard[]>} */
const cards = computed(() => {
  const d = state.data
  if (!d) return []
  return [
    {
      key: 'pm',
      label: 'PM Conducted',
      value: d.pm.conducted,
      caption: `${d.pm.finalized} finalized · ${d.pm.elevators} elev · ${d.pm.escalators} esc`,
      icon: 'mdi-clipboard-check-outline',
      color: 'teal darken-1',
    },
    {
      key: 'downtime',
      label: 'Open Downtime',
      value: d.downtime.open,
      caption: `${d.downtime.pending} pending · ${d.downtime.ongoing} ongoing`,
      icon: 'mdi-alert-circle-outline',
      color: 'orange darken-3',
    },
    {
      key: 'vouchers',
      label: 'Voucher Approvals',
      value: d.vouchers.pending_approval,
      caption: `pending of ${d.vouchers.total} vouchers`,
      icon: 'mdi-cash-clock',
      color: 'blue darken-2',
    },
  ]
})

const formatDateTime = value => {
  if (!value) return '—'
  const parsed = new Date(value.replace(' ', 'T'))
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const formatDate = value => {
  if (!value) return '—'
  const [year, month, day] = value.split('-')
  return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

const pesoFormatter = new Intl.NumberFormat('en-PH', {
  style: 'currency',
  currency: 'PHP',
  minimumFractionDigits: 2,
})
const formatPeso = value => pesoFormatter.format(Number(value) || 0)

/** Chip colour keyed on voucher_status_id so it survives description edits. */
const voucherStatusColor = statusId => {
  switch (statusId) {
    case 'NW': return 'blue-grey'
    case 'AP': return 'green'
    case 'RJ': return 'red'
    case 'CP': return 'teal'
    default: return 'grey'
  }
}

/** connection state of the live stream: 'connecting' | 'live' | 'offline' */
const streamStatus = ref('connecting')

/** @type {(() => void)|null} */
let closeStream = null

const streamChip = computed(() => {
  if (streamStatus.value === 'live') return { color: 'green', icon: 'mdi-access-point', label: 'Live' }
  if (streamStatus.value === 'connecting') return { color: 'grey', icon: 'mdi-sync', label: 'Connecting' }
  return { color: 'orange darken-2', icon: 'mdi-access-point-off', label: 'Offline' }
})

onBeforeMount(() => {
  // Fetch once for a fast first paint, then let the stream take over. The stream
  // also sends the current state on connect, so a slow first fetch is not fatal.
  getDashboard()

  closeStream = openStream('/v3/dashboard/stream', {
    onOpen: () => {
      streamStatus.value = 'live'
    },
    onEvent: (event, payload) => {
      if (event === 'stats' && payload) {
        state.data = payload
        state.error = null
      }
      // 'reconnect' needs no handling -- the client reconnects on stream close.
    },
    onError: () => {
      streamStatus.value = 'offline'
    },
  })
})

onBeforeUnmount(() => {
  controller?.abort()
  if (retryTimer) clearTimeout(retryTimer)
  if (closeStream) closeStream()
})
</script>

<template>
  <v-container fluid class="dashboard">
    <div class="dashboard-header">
      <div>
        <h1 class="dashboard-title">Dashboard</h1>
        <span v-if="monthLabel" class="dashboard-subtitle">{{ monthLabel }}</span>
      </div>
      <div class="header-actions">
        <v-chip x-small :color="streamChip.color" dark>
          <v-icon x-small left>{{ streamChip.icon }}</v-icon>
          {{ streamChip.label }}
        </v-chip>
        <v-btn small outlined :loading="state.isLoading" @click="getDashboard">
          <v-icon small left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </div>
    </div>

    <v-alert v-if="state.error" type="error" dense outlined class="mb-4">
      {{ state.error }} — retrying…
    </v-alert>

    <!-- Stat cards -->
    <div class="stat-grid">
      <template v-if="!state.data">
        <v-skeleton-loader v-for="n in 4" :key="n" type="card" class="stat-skeleton" />
      </template>
      <v-card v-for="card in cards" :key="card.key" outlined class="stat-card">
        <div class="stat-card-body">
          <v-avatar :color="card.color" size="42" class="stat-icon">
            <v-icon dark>{{ card.icon }}</v-icon>
          </v-avatar>
          <div class="stat-text">
            <div class="stat-value">{{ card.value }}</div>
            <div class="stat-label">{{ card.label }}</div>
            <div class="stat-caption">{{ card.caption }}</div>
          </div>
        </div>
      </v-card>
    </div>

    <p v-if="state.data" class="pm-note">
      PM counts reflect records conducted this month. The system has no PM schedule, so there
      is no target to compare against.
    </p>

    <!-- Panels -->
    <div class="panel-grid">
      <v-card outlined class="panel">
        <v-card-title class="panel-title">
          Open Downtime Reports
          <v-chip v-if="state.data" x-small class="ml-2">{{ state.data.downtime.open }}</v-chip>
          <v-spacer />
          <v-chip
            v-if="state.data && state.data.downtime.purchase_needed"
            x-small
            color="amber darken-3"
            dark
          >
            {{ state.data.downtime.purchase_needed }} awaiting parts
          </v-chip>
        </v-card-title>
        <v-divider />
        <v-skeleton-loader v-if="!state.data" type="list-item-two-line@3" />
        <v-list v-else-if="state.data.downtime.recent.length" dense>
          <v-list-item v-for="row in state.data.downtime.recent" :key="row.id">
            <v-list-item-content>
              <v-list-item-title class="row-title">
                {{ row.unit_id }} — {{ row.customer_name || 'Unknown customer' }}
              </v-list-item-title>
              <v-list-item-subtitle class="row-subtitle">
                {{ row.reported_issue }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="row-action">
              <v-chip x-small outlined>{{ row.status }}</v-chip>
              <span class="row-date">{{ formatDateTime(row.date_start) }}</span>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <div v-else class="panel-empty">Nothing open. All clear.</div>
      </v-card>

      <v-card outlined class="panel">
        <v-card-title class="panel-title">
          Latest Vouchers
          <v-spacer />
          <v-btn small text color="primary" :to="{ name: 'vouchers' }">
            All vouchers
            <v-icon small right>mdi-arrow-right</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-skeleton-loader v-if="!state.data" type="list-item-two-line@3" />
        <v-list v-else-if="state.data.vouchers.recent.length" dense>
          <v-list-item v-for="row in state.data.vouchers.recent" :key="row.id">
            <v-list-item-content>
              <v-list-item-title class="row-title">
                {{ row.voucher_no }} — {{ row.pay_to_name || 'Unknown payee' }}
              </v-list-item-title>
              <v-list-item-subtitle class="row-subtitle">
                {{ formatPeso(row.amount) }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="row-action">
              <v-chip x-small :color="voucherStatusColor(row.status_id)" dark>{{ row.status }}</v-chip>
              <span class="row-date">{{ formatDate(row.date) }}</span>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <div v-else class="panel-empty">No vouchers yet.</div>
      </v-card>
    </div>
  </v-container>
</template>

<style lang="sass" scoped>
.dashboard
  display: flex
  flex-direction: column
  gap: 16px

  .dashboard-header
    display: flex
    align-items: flex-start
    justify-content: space-between
    gap: 12px

  .header-actions
    display: flex
    align-items: center
    gap: 8px

  .dashboard-title
    font-size: 22px
    font-weight: 600
    line-height: 1.2

  .dashboard-subtitle
    font-size: 13px
    color: #777

  .stat-grid
    display: grid
    grid-template-columns: repeat(auto-fit, minmax(210px, 1fr))
    gap: 12px

  .stat-skeleton
    min-height: 96px

  .stat-card
    border-radius: 10px

  .stat-card-body
    display: flex
    align-items: center
    gap: 12px
    padding: 14px

  .stat-icon
    flex-shrink: 0

  .stat-text
    min-width: 0

  .stat-value
    font-size: 26px
    font-weight: 600
    line-height: 1.1

  .stat-label
    font-size: 12px
    font-weight: 600
    text-transform: uppercase
    letter-spacing: 0.4px
    color: #555

  .stat-caption
    font-size: 11px
    color: #8a8a8a
    overflow: hidden
    text-overflow: ellipsis

  .pm-note
    font-size: 11px
    color: #999
    margin: -4px 0 0

  .panel-grid
    display: grid
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr))
    gap: 12px

  .panel
    border-radius: 10px
    overflow: hidden

  .panel-title
    font-size: 14px
    font-weight: 600
    padding: 12px 16px

  .panel-empty
    padding: 24px 16px
    text-align: center
    font-size: 13px
    color: #999

  .row-title
    font-size: 13px
    font-weight: 500

  .row-subtitle
    font-size: 12px

  .row-action
    align-items: flex-end
    gap: 4px

  .row-date
    font-size: 11px
    color: #999
    white-space: nowrap
</style>

<script>
export default {
  name: 'Dashboard',
}
/**
 * @typedef {Object} DashboardData
 * @property {string} month
 * @property {{ conducted: number, finalized: number, elevators: number, escalators: number }} pm
 * @property {{ open: number, pending: number, ongoing: number, purchase_needed: number, recent: DowntimeRow[] }} downtime
 * @property {{ pending_approval: number, total: number, recent: VoucherRow[] }} vouchers
 *
 * @typedef {Object} DowntimeRow
 * @property {number} id
 * @property {string|null} form_number
 * @property {string} unit_id
 * @property {string|null} customer_name
 * @property {string} status
 * @property {string|null} date_start
 * @property {string} reported_issue
 *
 * @typedef {Object} VoucherRow
 * @property {number} id
 * @property {string} voucher_no
 * @property {string|null} pay_to_name
 * @property {number} amount
 * @property {string|null} status
 * @property {string} status_id
 * @property {string|null} date
 *
 * @typedef {Object} StatCard
 * @property {string} key
 * @property {string} label
 * @property {number} value
 * @property {string} caption
 * @property {string} icon
 * @property {string} color
 *
 * @typedef {Object} DashboardState
 * @property {DashboardData|null} data
 * @property {boolean} isLoading
 * @property {string|null} error
 */
</script>
